/**
 * Which calendar day it is, where the shop is.
 *
 * Every date below the UI is a zone-free `YYYY-MM-DD` string, and the date
 * arithmetic on them (`addDays`, blocks) works in UTC so that it never drifts.
 * The one place a zone has to come in is the step from "now" to "today": the
 * shop is in Wisconsin, and a UTC date turns over at 7 PM there in summer and
 * 6 PM in winter — so an evening import used to land in tomorrow's Day draft
 * and mark every project as last seen tomorrow. That step is made here, once,
 * and nowhere else.
 *
 * Stored timestamps are not touched by any of this: a `timestamptz` is an
 * instant, and stays UTC.
 */

/** The shop's zone when nothing configures one. Sentry Equipment is in Wisconsin. */
export const DEFAULT_TIME_ZONE = "America/Chicago";

/**
 * The zone, or a clear refusal.
 *
 * `Intl` throws a bare RangeError for a zone it does not know, and a zone that
 * is silently ignored would put the server back on UTC dates with nothing to
 * say so — the very bug the setting exists to fix. So a bad zone stops the
 * router being built at all, naming the setting and the value.
 */
export function assertTimeZone(timeZone: string): string {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
  } catch {
    throw new Error(
      `SENTRY_TIMEZONE "${timeZone}" is not a time zone this server knows. ` +
      `Use an IANA name such as "${DEFAULT_TIME_ZONE}".`,
    );
  }
  return timeZone;
}

/**
 * The calendar day `now` falls on in `timeZone`, as `YYYY-MM-DD`.
 *
 * en-CA is chosen for its format, which is already ISO order; the parts are
 * still reassembled rather than trusting the joined string, so a locale-data
 * change cannot slip a different separator in.
 */
export function localDay(now: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now);
  const part = (type: string) => parts.find((p) => p.type === type)!.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/**
 * `now` as `YYYY-MM-DD HH:MM` on the shop's clock — for a name a person reads,
 * such as "Published 2026-09-24 19:05", which should be the time they clicked.
 */
export function localMinute(now: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(now);
  const part = (type: string) => parts.find((p) => p.type === type)!.value;
  return `${localDay(now, timeZone)} ${part("hour")}:${part("minute")}`;
}
